// =============================================================================
//  useActiveTenant.ts - hybride live/demo
//
//  - En live  : useQuery GET /api/clients, expose la liste reelle des clients
//  - En demo  : utilise mockTenants statiques
//
//  L API publique du hook est STABLE entre les deux modes (les pages ne savent
//  pas dans quel mode elles tournent).
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tenant } from '../domain/types';
import { defaultTenantId, mockTenants } from '../domain/mockTenants';
import { IS_LIVE_MODE } from '../api/liveMode';
import { nis365Api } from '../api/nis365Client';

const STORAGE_KEY  = 'nis365.activeTenantId';
const CHANGE_EVENT = 'nis365:tenant-changed';

function readPreferredTenantId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

export function useActiveTenant() {
  const [tenantId, setTenantIdState] = useState<string | null>(readPreferredTenantId);

  // En live : fetch la liste depuis l API
  const liveQuery = useQuery({
    queryKey:  ['clients'],
    queryFn:   () => nis365Api.listClients(),
    enabled:   IS_LIVE_MODE,
    staleTime: 30_000,
  });

  // Resoud la liste effective des tenants (live ou demo)
  const tenants: Tenant[] = IS_LIVE_MODE
    ? (liveQuery.data ?? [])
    : mockTenants;

  // Determine le tenant actif :
  //  1. Si l ID stocke en LS existe encore dans la liste -> on le garde
  //  2. Sinon premier tenant de la liste
  //  3. Sinon fallback demo (utile si live retourne une liste vide pendant le
  //     chargement initial)
  const resolvedTenant: Tenant | undefined =
    (tenantId && tenants.find((t) => t.id === tenantId)) ||
    tenants[0] ||
    mockTenants.find((t) => t.id === defaultTenantId) ||
    mockTenants[0];

  // Synchronisation entre composants + onglets
  useEffect(() => {
    const handler = () => setTenantIdState(readPreferredTenantId());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const setTenantId = useCallback((id: string) => {
    window.localStorage.setItem(STORAGE_KEY, id);
    setTenantIdState(id);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return {
    tenant:    resolvedTenant ?? mockTenants[0],
    tenantId:  resolvedTenant?.id ?? mockTenants[0].id,
    setTenantId,
    tenants,
    // Etats live exposes pour les pages qui veulent montrer un loader
    isLoading: IS_LIVE_MODE ? liveQuery.isLoading : false,
    isError:   IS_LIVE_MODE ? liveQuery.isError   : false,
    error:     IS_LIVE_MODE ? liveQuery.error     : null,
  };
}
